/// <amd-module name="Build/Admin/Pool/Stores/AgentStore" />
import { Store } from "VSS/Reflux/Store";

import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { MakeOptional } from "VSS/Core/Util/Types";

import { TaskAgent, TaskAgentStatus, TaskAgentJobRequest } from "DistributedTask/Client/Contracts/TaskAgent";

export class AgentStore extends Store {
    public state: TaskAgent[] = [];

    constructor() {
        super("AgentStoreChanged");
    }

    public getAgent(taskAgentId: number): TaskAgent | undefined {
        if (this.state.length > 0) {
            const matchingAgents: TaskAgent[] = this.state.filter(agent => agent.id === taskAgentId);
            return matchingAgents[0];
        }

        return undefined;
    }

    public updateAgents = (agents: TaskAgent[]) => {
        const newAgents: TaskAgent[] = agents ? agents.slice(0) : [];

        this.state = this._sortAgents(newAgents);

        this.emitChanged();
    }

    public updateAgentStatus = (agentId: number, status: TaskAgentStatus) => {
        const agent: TaskAgent | undefined = this.getAgent(agentId);

        if (agent) {
            agent.status = status;
            this.updateAgent(agent);
        }
    }

    public updateAgent = (updatedAgent: TaskAgent) => {
        const otherAgents: TaskAgent[] = this.state.filter(agent => agent.id !== updatedAgent.id);
        otherAgents.push(updatedAgent);

        this.state = this._sortAgents(otherAgents);

        this.emitChanged();
    }

    public agentDeleted = (agent: TaskAgent) => {
        if (this.state.length > 0) {
            const otherAgents: TaskAgent[] = this.state.filter(a => a.id !== agent.id);

            this.state = otherAgents; // Shouldn't need to sort here, they're already sorted and removing one doesn't affect that

            this.emitChanged();
        }
    }

    public updateAssignedJobRequest = (job: TaskAgentJobRequest) => {
        if (this.state.length > 0 && job.reservedAgent) {
            const matchingAgent: TaskAgent | undefined = this.state.filter(agent => agent.id === job.reservedAgent.id)[0];

            if (matchingAgent) {
                const otherAgents: TaskAgent[] = this.state.filter(a => a.id !== matchingAgent.id);

                if (job.finishTime) {
                    // Job finished, so remove it from the agent's assignedRequest property
                    // and set it as the last completed request
                    // Note: this property is not nullable/undefine-able, so we have to delete it
                    delete (matchingAgent as MakeOptional<TaskAgent, "assignedRequest">).assignedRequest;
                    matchingAgent.lastCompletedRequest = job;
                }
                else {
                    // Job is running/assigned
                    matchingAgent.assignedRequest = job;
                }

                otherAgents.push(matchingAgent);

                this.state = this._sortAgents(otherAgents);

                this.emitChanged();
            }
        }
    }

    private _sortAgents(agents: TaskAgent[]): TaskAgent[] {
        return agents.sort((a: TaskAgent, b: TaskAgent) => {
            return localeIgnoreCaseComparer(a.name, b.name);
        });
    }
}
